<template>
  <div id="blog-content-wrapper" :style="fontFamilyStyle" class="blog-content-wrapper">
    <div class="ContentEditable__root">
      <div class="content" v-html="computedContent"></div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { usePlayView } from '~/features/pageList';

const props = withDefaults(
  defineProps<{
    content?: string;
  }>(),
  {
    content: '',
  },
);

// 添加emit定义
const emit = defineEmits<{
  'current-heading': [heading: string];
}>();

const fontFamilyStyle = computed(() => {
  return isChinese() ? {} : { fontFamily: 'Poppins, sans-serif' };
});

const DOMESTIC_URL = 'media.xmov.ai';
const OVERSEAS_URL = 'oss.rendora.ai';

const computedContent = computed(() => {
  // video 标签增加 loading lazy
  let content = props.content;
  content = content.replace(/<video/g, '<video loading="lazy" preload="metadata"');
  // audio 标签单独处理
  content = content.replace(/<audio/g, '<audio preload="metadata" controls');
  // 图片链接自动 oss webp 处理
  content = content.replace(/<img/g, '<img loading="lazy"');
  // 只匹配 <a 标签，不匹配 <audio 标签
  content = content.replace(/<a(?=\s|>)/g, '<a target="_blank"');
  // public-xmov.oss-cn-hangzhou.aliyuncs.com 替换为media.xmov.ai
  content = content.replace(
    /public-xmov\.oss-cn-hangzhou\.aliyuncs\.com/g,
    isChinese() ? DOMESTIC_URL : OVERSEAS_URL,
  );
  // xmov-youyan-hk.oss-cn-hongkong.aliyuncs.com 替换为oss.rendora.ai
  content = content.replace(/xmov-youyan-hk\.oss-cn-hongkong\.aliyuncs\.com/g, OVERSEAS_URL);
  // 识别资源链接并在原本链接后凭借 oss 的 webp 处理
  content = content.replace(/(<img[^>]*src=")([^"]*)([^>]*>)/g, (match, prefix, src, suffix) => {
    const lowerSrc = src.toLowerCase();
    // 检查是否是 OSS 图片链接
    if (lowerSrc.includes(DOMESTIC_URL) || lowerSrc.includes(OVERSEAS_URL)) {
      // 如果链接已经包含处理参数, 或为 png 和 jpg 格式，则不添加
      const isProcessable =
        !lowerSrc.includes('x-oss-process=') && // 不包含处理参数
        (lowerSrc.endsWith('.png') || lowerSrc.endsWith('.jpg') || lowerSrc.endsWith('.jpeg')); // 是 png 或 jpg 格式

      if (!isProcessable) {
        return match;
      }

      // 添加 webp 处理参数和最大宽度限制
      const separator = lowerSrc.includes('?') ? '&' : '?';
      return `${prefix}${src}${separator}x-oss-process=image/resize,w_800/format,webp${suffix}`;
    }
    return match;
  });

  return content;
});

onMounted(() => {
  onImgClick();
  observeHeadings();
});

// 添加标题观察功能
const observeHeadings = () => {
  const contentWrapper = document.getElementById('blog-content-wrapper');
  if (!contentWrapper) return;

  const headings = contentWrapper.querySelectorAll('h2, h3');
  if (headings.length === 0) return;

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const headingId = entry.target.id || '';
          emit('current-heading', headingId);
        }
      });
    },
    {
      root: null,
      rootMargin: '-50% 0px -50% 0px', // 当标题位于视窗中央时触发
      threshold: 0,
    },
  );

  headings.forEach(heading => {
    observer.observe(heading);
  });

  // 组件卸载时清理observer
  onUnmounted(() => {
    observer.disconnect();
  });
};

const onImgClick = () => {
  const editorShell = document.getElementById('blog-content-wrapper');
  if (editorShell) {
    const imgElements = editorShell.querySelectorAll('img');
    imgElements.forEach(function (img) {
      img.addEventListener('click', function () {
        const srcValue = this.src;

        usePlayView({ file_url: srcValue });
      });
    });
  }
};
</script>

<style lang="scss">
@use '@/assets/css/blogContent.scss';

.video-container-wrapper {
  width: 100%;
  height: auto;
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 16/9;

  .video-wrapper {
    width: 100%;
    height: auto;
    object-fit: fill;
  }
}
.blog-content-wrapper {
  img {
    cursor: pointer;
  }

  // li {
  //   list-style: none;
  //   position: relative;
  //   padding-left: 24px;
  //   margin-bottom: 12px;
  //   line-height: 1.6;
  //   color: var(--text-1);

  //   &::before {
  //     content: '•';
  //     position: absolute;
  //     left: 8px;
  //     color: var(--text-1);
  //   }

  //   /* 第二层 */
  //   > ul > li {
  //     list-style: none;
  //     position: relative;
  //     padding-left: 24px;
  //     margin: 8px 0;

  //     &::before {
  //       content: '○';
  //       position: absolute;
  //       left: 4px;
  //       transform: scale(0.6);
  //       color: var(--text-2);
  //     }

  //     /* 第三层 */
  //     > ul > li {
  //       list-style: none;
  //       position: relative;
  //       padding-left: 24px;
  //       margin: 6px 0;

  //       &::before {
  //         content: '■';
  //         position: absolute;
  //         transform: scale(1);
  //         left: 8px;
  //         font-size: 8px;
  //         top: 8px;
  //         color: var(--text-3);
  //       }
  //     }
  //   }
  // }

  h2,
  h3 {
    scroll-margin-top: 100px;
  }
}
</style>
